import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, lastValueFrom, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticateRequest } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/authenticate-request';
import { AuthenticateResponse } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/authenticate-response';
import { ChangePasswordRequest } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/change-password-request';
import { DestroyAccountRequest } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/destroy-account-request';
import { ForgotPasswordRequest } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/forgot-password-request';
import { ResetPasswordRequest } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/reset-password-request';
import { VerifyAccountRequest } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/verify-account-request';
import { VerifyDestroyTokenRequest } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/verify-destroy-token-request';
import { VerifyResetTokenRequest } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/verify-reset-token-request';
import { SimpleResponse } from 'src/app/backend-client/models/SharedKernel/Models/simple-response';
import { IdentityBackendService } from 'src/app/backend-client/services';

import { MxLoggingService } from './mx.logging.service';
import { MxSignalRService } from './mx.signal-r.service';

@Injectable({ providedIn: 'root' })
export class MxIdentityService {
  private accountSubject: BehaviorSubject<AuthenticateResponse>;
  public account: Observable<AuthenticateResponse>;

  constructor(
    private router: Router,
    private identityBackendService: IdentityBackendService,
    private loggingService: MxLoggingService,
    private signalRService: MxSignalRService
  ) {
    this.accountSubject = new BehaviorSubject<AuthenticateResponse>(null);
    this.account = this.accountSubject.asObservable();
  }

  public get accountValue(): AuthenticateResponse {
    return this.accountSubject.value;
    // return {
    //   correlationId: '',
    //   createdOn: "",
    //   email: 'demo@demo.com',
    //   firstName: 'Demo',
    //   id: 0,
    //   isVerified: true,
    //   jwtToken: null,
    //   lastName: 'Felhasználó',
    //   refreshToken: null,
    //   role: null,
    //   settings: { isFeature1Enabled: false, isFeature2Enabled: false },
    //   subscription: { id: 0, name: '', type: SubscriptionType.Free },
    //   updatedOn: null,
    // };
  }

  preAuthenticate(input: AuthenticateRequest): Observable<SimpleResponse> {
    return this.identityBackendService.preAuthenticate$Json({
      body: input,
    });
  }

  login(input: AuthenticateRequest): Observable<AuthenticateResponse> {
    return this.identityBackendService
      .authenticate$Json({
        body: input,
      })
      .pipe(
        map((account) => {
          this.accountSubject.next(account);
          this.startRefreshTokenTimer();
          // this.signalRService.start(account.email);
          return account;
        })
      );
  }

  loginAfterVerifyAccount(
    input: AuthenticateResponse
  ): Observable<AuthenticateResponse> {
    this.accountSubject.next(input);
    this.startRefreshTokenTimer();
    // this.signalRService.start(input.email);
    return of(input);
  }

  logout(withoutNavigateToLogin: boolean = false): void {
    if (this.accountValue) {
      this.identityBackendService.revokeToken$Json$Response().subscribe();
    }
    this.stopRefreshTokenTimer();
    // this.signalRService.stop();
    this.accountSubject.next(null);
    if (!withoutNavigateToLogin) {
      this.router.navigate(['pages', 'login']);
    }
  }

  refreshToken(): Observable<AuthenticateResponse> {
    return this.identityBackendService.refreshToken$Json().pipe(
      map((account) => {
        this.accountSubject.next(account);
        if (account) {
          this.startRefreshTokenTimer();
          // if (
          //   this.signalRService.hubConnection?.state !== 'Connecting' &&
          //   this.signalRService.hubConnection?.state !== 'Connected'
          // ) {
          //   this.signalRService.start(account.email);
          // }
          this.loggingService.setAppInsightsAuthenticatedUserContext(account);
        }
        return account;
      })
    );
  }

  async refreshTokenLoad(): Promise<void> {
    await lastValueFrom(this.refreshToken()).catch((err) => {
      this.loggingService.info(err);
    });
  }

  // register(input: RegisterRequest): Observable<SimpleResponse> {
  //   return this.identityBackendService.register$Json({ body: input });
  // }

  verifyAccount(input: VerifyAccountRequest): Observable<SimpleResponse> {
    return this.identityBackendService.verifyAccount$Json({ body: input });
  }

  verifyAccountThenAuthenticate(
    input: VerifyAccountRequest
  ): Observable<AuthenticateResponse> {
    return this.identityBackendService.verifyAccountThenAuthenticate$Json({
      body: input,
    });
  }

  forgotPassword(input: ForgotPasswordRequest): Observable<SimpleResponse> {
    return this.identityBackendService.forgotPassword$Json({ body: input });
  }

  verifyResetToken(input: VerifyResetTokenRequest): Observable<SimpleResponse> {
    return this.identityBackendService.verifyResetToken$Json({ body: input });
  }

  resetPassword(input: ResetPasswordRequest): Observable<SimpleResponse> {
    return this.identityBackendService.resetPassword$Json({ body: input });
  }

  changePassword(input: ChangePasswordRequest): Observable<SimpleResponse> {
    return this.identityBackendService.changePassword$Json({ body: input });
  }

  initiateDestroyAccount(): Observable<SimpleResponse> {
    return this.identityBackendService.initiateDestroyAccount$Json();
  }

  verifyDestroyToken(
    input: VerifyDestroyTokenRequest
  ): Observable<SimpleResponse> {
    return this.identityBackendService.verifyDestroyToken$Json({ body: input });
  }

  destroyAccount(input: DestroyAccountRequest): Observable<SimpleResponse> {
    return this.identityBackendService.destroyAccount$Json({ body: input });
  }

  // helper methods

  private refreshTokenTimeout: any;

  private startRefreshTokenTimer() {
    // parse json object from base64 encoded jwt token

    const jwtToken = JSON.parse(atob(this.accountValue.jwtToken.split('.')[1]));

    // set a timeout to refresh the token a minute before it expires
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(
      () => this.refreshToken().subscribe(),
      timeout
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
